<template>
  <div>
    <reports-user v-if="isUserRole" />
    
    <v-container
      v-else
      id="reports"
      fluid
      tag="section"
      class="dashboard-container"
      style="padding: 24px 96px;"
    >
      <v-row class="mb-2 ma-n2" align="center">
        <v-col cols="12" sm="auto" class="pa-2">
          <div class="d-flex align-center">
            <v-icon color="#00E5FF" large class="mr-3">mdi-file-chart-outline</v-icon>
            <h1 class="text-h5 font-weight-bold text--primary">{{ $t('reports.title') }}</h1>
          </div>
        </v-col>
      </v-row>

      <v-card class="elevment-card mb-4 pa-4 elevation-0">
        <v-row class="mb-3">
          <v-col cols="12" md="3">
            <v-text-field
              v-model="searchQuery"
              prepend-inner-icon="mdi-magnify"
              :label="$t('reports.searchReport')"
              outlined
              dense
              hide-details
              clearable
              color="#00E5FF"
              class="elevment-input"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="selectedDepartment"
              :items="departments"
              item-text="name"
              item-value="id"
              :label="$t('reports.allDepartments')"
              prepend-inner-icon="mdi-domain"
              outlined
              dense
              hide-details
              clearable
              color="#00E5FF"
              class="elevment-input"
              @change="onDepartmentChange"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="selectedTeam"
              :items="teams"
              item-text="name"
              item-value="id"
              label="Todos os Times"
              prepend-inner-icon="mdi-account-group"
              outlined
              dense
              hide-details
              clearable
              color="#00E5FF"
              class="elevment-input"
              @change="onTeamChange"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="selectedUser"
              :items="filteredUsers"
              item-text="name"
              item-value="id"
              label="Todos os Usuários"
              prepend-inner-icon="mdi-account"
              outlined
              dense
              hide-details
              clearable
              color="#00E5FF"
              class="elevment-input"
            >
              <template v-slot:item="{ item }">
                <v-list-item-avatar size="28">
                  <v-avatar :color="item.avatarUrl ? 'transparent' : getAvatarColor(item.name)" size="28">
                    <v-img v-if="item.avatarUrl" :src="item.avatarUrl" :alt="item.name" />
                    <span v-else class="white--text" style="font-size: 11px">{{ getInitials(item.name) }}</span>
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">{{ item.name }}</v-list-item-title>
                </v-list-item-content>
              </template>
              <template v-slot:selection="{ item }">
                <div class="d-flex align-center">
                  <v-avatar :color="item.avatarUrl ? 'transparent' : getAvatarColor(item.name)" size="24" class="mr-2">
                    <v-img v-if="item.avatarUrl" :src="item.avatarUrl" :alt="item.name" />
                    <span v-else class="white--text" style="font-size: 9px">{{ getInitials(item.name) }}</span>
                  </v-avatar>
                  <span class="text-body-2">{{ item.name }}</span>
                </div>
              </template>
            </v-select>
          </v-col>
        </v-row>

        <v-row class="mb-0">
          <v-col cols="12" md="6">
            <v-select
              v-model="selectedPeriod"
              :items="periodOptions"
              :label="selectedPeriodLabel"
              prepend-inner-icon="mdi-calendar"
              outlined
              dense
              hide-details
              clearable
              color="#00E5FF"
              class="elevment-input"
            />
          </v-col>

          <v-col cols="12" md="6">
            <v-select
              v-model="selectedTypes"
              :items="fileTypeOptions"
              label="Todos os formatos"
              prepend-inner-icon="mdi-file-document-outline"
              outlined
              dense
              hide-details
              multiple
              clearable
              color="#00E5FF"
              class="elevment-input"
            >
              <template v-slot:selection="{ index }">
                <span v-if="index === 0" class="text-caption">
                  {{ selectedTypes.length === fileTypeOptions.length ? 'Todos formatos' : selectedTypes.length === 0 ? 'Todos...' : `${selectedTypes.length} selecionado${selectedTypes.length > 1 ? 's' : ''}` }}
                </span>
              </template>
              <template v-slot:prepend-item>
                <v-list-item @click="toggleAllFormats" class="all-formats-item">
                  <v-list-item-action>
                    <v-icon :color="allFormatsSelected ? '#00E5FF' : ''">
                      {{ allFormatsSelected ? 'mdi-check' : 'mdi-checkbox-blank-outline' }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">Todos formatos</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-2" />
              </template>
              <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-bind="attrs" v-on="on">
                  <v-list-item-action>
                    <v-checkbox :input-value="attrs.inputValue" color="#00E5FF" hide-details />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-select>
          </v-col>
        </v-row>
      </v-card>

      <v-chip-group
        v-model="selectedCategory"
        mandatory
        class="mb-4"
      >
        <v-chip
          v-for="category in categoriesWithAll"
          :key="category.value"
          :value="category.value"
          outlined
          :class="[
            'font-weight-bold category-filter-chip',
            selectedCategory === category.value ? 'is-active' : 'is-inactive'
          ]"
        >
          <v-icon left small>{{ category.icon }}</v-icon>
          {{ category.label }}
          <v-chip
            v-if="category.count"
            x-small
            :class="[
              'ml-2 category-count-badge',
              selectedCategory === category.value ? 'is-active' : 'is-inactive'
            ]"
          >
            {{ category.count }}
          </v-chip>
        </v-chip>
      </v-chip-group>

      <v-row>
        <v-col
          v-for="report in filteredReports"
          :key="report.id"
          cols="12"
          md="6"
          lg="4"
        >
          <v-card class="report-card elevment-card elevation-0" hover>
            <v-card-text>
              <div class="d-flex align-start">
                <v-avatar size="48" :color="report.iconColor" class="mr-3">
                  <v-icon color="white">{{ report.icon }}</v-icon>
                </v-avatar>

                <div class="flex-grow-1">
                  <h3 class="text-h6 font-weight-bold text--primary mb-1">{{ report.title }}</h3>
                  <div class="text-caption grey--text text--darken-1 mb-2 font-weight-medium text-uppercase">
                    {{ report.category }}
                  </div>
                  <p class="text-body-2 text--secondary mb-3" style="line-height: 1.5">{{ report.description }}</p>

                  <div class="mb-3 d-flex flex-wrap">
                    <v-chip
                      v-for="tag in report.tags"
                      :key="tag"
                      x-small
                      color="rgba(128,128,128,0.1)"
                      class="mr-1 mb-1 text--secondary text-uppercase"
                    >
                      {{ tag }}
                    </v-chip>
                    
                    <v-tooltip bottom v-if="userScopedSlugs.includes(report.slug) || userScopedCategories.includes(report.category)">
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip
                          x-small
                          color="rgba(0, 229, 255, 0.1)"
                          text-color="#00E5FF"
                          class="mr-1 mb-1 font-weight-bold"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon x-small left>mdi-account-check</v-icon>
                          Por usuário
                        </v-chip>
                      </template>
                      <span>Este relatório é gerado por usuário específico</span>
                    </v-tooltip>
                  </div>

                  <div class="d-flex flex-wrap gap-2">
                    <v-btn
                      v-if="report.formats.includes('pdf') && isFormatVisible('pdf')"
                      x-small
                      outlined
                      color="#00E5FF"
                      class="font-weight-bold text-none"
                      :disabled="!!downloadingKey"
                      :loading="downloadingKey === `${report.id}-pdf`"
                      @click="downloadReport(report, 'pdf')"
                    >
                      <v-icon left x-small>mdi-download</v-icon>
                      PDF
                    </v-btn>
                    <v-btn
                      v-if="report.formats.includes('xlsx') && isFormatVisible('xlsx')"
                      x-small
                      outlined
                      color="#00E5FF"
                      class="font-weight-bold text-none"
                      :disabled="!!downloadingKey"
                      :loading="downloadingKey === `${report.id}-xlsx`"
                      @click="downloadReport(report, 'xlsx')"
                    >
                      <v-icon left x-small>mdi-download</v-icon>
                      XLSX
                    </v-btn>
                    <v-btn
                      v-if="report.formats.includes('csv') && isFormatVisible('csv')"
                      x-small
                      outlined
                      color="#00E5FF"
                      class="font-weight-bold text-none"
                      :disabled="!!downloadingKey"
                      :loading="downloadingKey === `${report.id}-csv`"
                      @click="downloadReport(report, 'csv')"
                    >
                      <v-icon left x-small>mdi-download</v-icon>
                      CSV
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="!loading && filteredReports.length === 0">
        <v-col cols="12" class="text-center py-12">
          <v-icon size="64" color="rgba(128,128,128,0.2)">mdi-file-document-outline</v-icon>
          <h3 class="text-h6 mt-4 text--primary">Nenhum relatório encontrado</h3>
          <p class="text-body-2 text--secondary">Tente ajustar os filtros para ver mais resultados</p>
        </v-col>
      </v-row>

      <v-overlay :value="loading" absolute class="rounded-lg">
        <v-progress-circular indeterminate size="48" color="#00E5FF" width="4" />
      </v-overlay>
    </v-container>
  </div>
</template>

<script>
import { ReportsService, TeamsService, OrganizationService, UsersService } from '@/services'
import ReportsUser from './ReportsUser.vue'

export default {
  name: 'Reports',

  components: { ReportsUser },

  data: () => ({
    loading: false,
    downloading: false,
    downloadingKey: null,
    searchQuery: '',
    selectedDepartment: null,
    selectedTeam: null,
    selectedUser: null,
    selectedPeriod: null,
    selectedTypes: [],
    selectedCategory: null,

    // Dados da API
    reports: [],
    categories: [],
    departments: [],
    teams: [],
    users: [],

    // Opções de filtros
    periodOptions: [
      { text: 'Últimos 7 dias', value: 'last_7_days' },
      { text: 'Últimos 30 dias', value: 'last_30_days' },
      { text: 'Últimos 90 dias', value: 'last_90_days' },
      { text: 'Últimos 6 meses', value: 'last_6_months' },
      { text: 'Último ano', value: 'last_year' },
    ],

    fileTypeOptions: [
      { text: 'PDF', value: 'pdf' },
      { text: 'Excel (XLSX)', value: 'xlsx' },
      { text: 'CSV', value: 'csv' },
    ],

    // Reports que exigem seleção de um usuário específico para serem exibidos
    userScopedSlugs: [
      'adesao-pdis',
      'relatorio-one-on-ones',
      'relatorio-feedbacks',
    ],
    userScopedCategories: [
      'engenharia',
    ],
  }),

  mounted() {
    this.loadReports()
    this.loadDepartments()
    this.loadTeams()
    this.loadUsers()
  },

  watch: {
    // Limpa seleção de usuário se ele não constar mais na lista filtrada
    filteredUsers(newList) {
      if (this.selectedUser && !newList.find(u => u.id === this.selectedUser)) {
        this.selectedUser = null
      }
    },
  },

  computed: {
    isUserRole() {
      try {
        const userStr = localStorage.getItem('user')
        if (userStr) {
          const user = JSON.parse(userStr)
          return user.role === 'USER'
        }
      } catch {
        // noop
      }
      return false
    },

    allFormatsSelected() {
      return this.selectedTypes.length === this.fileTypeOptions.length
    },

    selectedPeriodLabel() {
      if (!this.selectedPeriod) return 'Período'
      const opt = this.periodOptions.find(o => o.value === this.selectedPeriod)
      return opt ? opt.text : 'Período'
    },

    categoriesWithAll() {
      // Base: aplicar todos os filtros ativos EXCETO categoria, para contar por categoria corretamente
      let base = this.reports

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        base = base.filter(r =>
          r.title.toLowerCase().includes(query) ||
          r.description.toLowerCase().includes(query) ||
          r.tags.some(tag => tag.toLowerCase().includes(query))
        )
      }

      const activeTypes = this.selectedTypes.filter(t => t)
      if (activeTypes.length > 0 && activeTypes.length < this.fileTypeOptions.length) {
        base = base.filter(r => activeTypes.some(type => r.formats.includes(type)))
      }

      if (this.selectedUser) {
        base = base.filter(r =>
          this.userScopedSlugs.includes(r.slug) ||
          this.userScopedCategories.includes(r.category)
        )
      }

      const allCategory = { label: 'Todas', value: null, icon: 'mdi-file-document-outline', count: base.length }

      const withCounts = this.categories.map(cat => ({
        ...cat,
        count: base.filter(r => r.category === cat.value).length,
      }))

      return [allCategory, ...withCounts]
    },

    // Filtrar usuários com role USER + por departamento OU time selecionado
    filteredUsers() {
      let users = this.users.filter(user => user.role === 'USER')

      if (this.selectedDepartment) {
        users = users.filter(user => user.department?.id === this.selectedDepartment)
      } else if (this.selectedTeam) {
        users = users.filter(user => {
          if (!user.teams || !user.teams.length) return false
          return user.teams.some(t =>
            typeof t === 'object' ? (t.id === this.selectedTeam || t._id === this.selectedTeam) : t === this.selectedTeam
          )
        })
      }

      return users
    },
    
    filteredReports() {
      let reports = this.reports.map(r => ({
        ...r,
        iconColor: r.icon_color,
      }))

      // Filtrar por categoria (null = todas)
      if (this.selectedCategory !== null) {
        reports = reports.filter(r => r.category === this.selectedCategory)
      }

      // Filtrar por busca
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        reports = reports.filter(r => 
          r.title.toLowerCase().includes(query) ||
          r.description.toLowerCase().includes(query) ||
          r.tags.some(tag => tag.toLowerCase().includes(query))
        )
      }

      // Filtrar por tipos de arquivo (nenhum selecionado OU todos selecionados = exibe tudo)
      const activeTypes = this.selectedTypes.filter(t => t)
      if (activeTypes.length > 0 && activeTypes.length < this.fileTypeOptions.length) {
        reports = reports.filter(r =>
          activeTypes.some(type => r.formats.includes(type))
        )
      }

      // Quando um usuário específico é selecionado, exibir apenas relatórios relacionados a ele
      if (this.selectedUser) {
        reports = reports.filter(r =>
          this.userScopedSlugs.includes(r.slug) ||
          this.userScopedCategories.includes(r.category)
        )
      }

      return reports
    },
  },

  methods: {
    async loadReports() {
      this.loading = true
      try {
        const response = await ReportsService.list()
        const allFormats = ['pdf', 'xlsx', 'csv']
        this.reports = (response.data.reports || []).map(r => ({
          ...r,
          formats: allFormats,
        }))
        this.categories = response.data.categories || []
        console.log('✅ Relatórios carregados:', this.reports.length)
      } catch (error) {
        console.error('❌ Erro ao carregar relatórios:', error)
        this.$store.commit('SET_SNACKBAR', {
          show: true,
          color: 'error',
          text: 'Erro ao carregar relatórios',
        })
      } finally {
        this.loading = false
      }
    },

    async loadDepartments() {
      try {
        const response = await OrganizationService.departments.list()
        const allDepartments = response.data || []
        this.departments = allDepartments.filter(dept => dept.active === true)
        console.log('✅ Departamentos carregados:', this.departments.length)
      } catch (error) {
        console.error('❌ Erro ao carregar departamentos:', error)
      }
    },

    async loadTeams() {
      try {
        const response = await TeamsService.list()
        const allTeams = response.data || []
        this.teams = allTeams.filter(team => team.status !== 'ARCHIVED')
        console.log('✅ Times carregados:', this.teams.length)
      } catch (error) {
        console.error('❌ Erro ao carregar times:', error)
      }
    },

    async loadUsers() {
      try {
        const response = await UsersService.list()
        this.users = response.data || []
        console.log('✅ Usuários carregados:', this.users.length)
      } catch (error) {
        console.error('❌ Erro ao carregar usuários:', error)
      }
    },

    onDepartmentChange(value) {
      if (value) {
        this.selectedTeam = null
      }
    },

    onTeamChange(value) {
      if (value) {
        this.selectedDepartment = null
      }
    },

    toggleAllFormats() {
      if (this.allFormatsSelected) {
        this.selectedTypes = []
      } else {
        this.selectedTypes = this.fileTypeOptions.map(opt => opt.value)
      }
    },
    
    isFormatVisible(format) {
      if (!this.selectedTypes.length || this.selectedTypes.length === this.fileTypeOptions.length) return true
      return this.selectedTypes.includes(format)
    },

    getAvatarColor(name) {
      if (!name) return '#00E5FF'
      const colors = ['#00E5FF', '#2196F3', '#FF9800', '#9C27B0', '#00BCD4', '#E91E63', '#607D8B', '#795548']
      const index = name.charCodeAt(0) % colors.length
      return colors[index]
    },

    getInitials(name) {
      if (!name) return '?'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },

    async downloadReport(report, format) {
      if (this.downloadingKey) return

      this.downloadingKey = `${report.id}-${format}`
      this.$store.commit('SET_SNACKBAR', {
        show: true,
        color: 'info',
        text: `Gerando ${report.title} em formato ${format.toUpperCase()}...`,
      })

      try {
        const config = {
          format: format,
          period: this.selectedPeriod,
          filters: {}, 
        }

        if (this.selectedDepartment) {
          config.department_id = String(this.selectedDepartment)
        }

        if (this.selectedTeam) {
          config.team_id = String(this.selectedTeam)
        }

        if (this.selectedUser) {
          config.user_id = String(this.selectedUser)
        }

        console.log('🔹 Gerando relatório:', {
          reportId: report.id,
          slug: report.slug,
          config: config,
        })

        await ReportsService.downloadReport(report.id, report.title, config)
        
        this.$store.commit('SET_SNACKBAR', {
          show: true,
          color: 'success',
          text: `✅ Download de ${report.title} concluído!`,
        })
      } catch (error) {
        console.error('❌ Erro ao baixar relatório:', error)
        this.$store.commit('SET_SNACKBAR', {
          show: true,
          color: 'error',
          text: 'Erro ao baixar relatório. Tente novamente.',
        })
      } finally {
        this.downloadingKey = null
      }
    },
  },
}
</script>

<style scoped>
/* Fundo Global Premium */
.dashboard-container {
  max-width: 1440px;
  margin: 0 auto;
}

/* Base Cards */
.elevment-card {
  border-radius: 12px !important;
  border: 1px solid var(--border-color, rgba(128,128,128,0.15)) !important;
  background-color: var(--bg-card, #FFFFFF) !important;
}

/* Estilização dos Inputs Clean */
::v-deep .elevment-input .v-input__control {
  border-radius: 8px !important;
}

/* Report Cards */
.report-card {
  height: 100%;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.report-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08) !important;
}

.gap-2 {
  gap: 8px;
}

/* Chips e Tabs */
.category-filter-chip {
  border-radius: 999px !important;
  height: 34px !important;
  transition: all 0.2s ease;
}

.category-filter-chip .v-icon {
  color: inherit !important;
}

.category-filter-chip.is-inactive {
  background-color: #121417 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #A9AFB8 !important;
}

.category-filter-chip.is-active {
  background-color: #23D4F4 !important;
  border-color: #23D4F4 !important;
  color: #071117 !important;
}

.category-count-badge {
  min-width: 20px;
  height: 20px;
  border-radius: 999px !important;
  padding: 0 6px !important;
  font-weight: 700 !important;
  border: 1px solid transparent !important;
}

.category-count-badge.is-inactive {
  background-color: #1B1F24 !important;
  color: #8C929B !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.category-count-badge.is-active {
  background-color: #12BFD9 !important;
  color: #06232B !important;
}

/* Modo Dark Fallback para o Badge */
.theme--dark .category-filter-chip.is-inactive {
  background-color: #121417 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #A9AFB8 !important;
}

.theme--dark .category-filter-chip.is-active {
  background-color: #23D4F4 !important;
  border-color: #23D4F4 !important;
  color: #071117 !important;
}

/* Estilo para o item "Todos formatos" no dropdown */
.all-formats-item {
  background-color: rgba(0, 229, 255, 0.05) !important;
  transition: background-color 0.2s ease;
}

.all-formats-item:hover {
  background-color: rgba(0, 229, 255, 0.1) !important;
}

.all-formats-item .v-list-item__title {
  color: #00E5FF !important;
}
</style>